import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAbility } from '@casl/react';
import { Edit, FileDown, Package, Plus, Power, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { ListStatusFilter } from '../../types/lists.types';
import { Product, ProductOptions } from '../../types/stock.types';
import { productsApi } from '../../utils/stock-api';
import { cn } from '../../components/ui/utils';
import StatusPill from '../lists/components/StatusPill';
import ProductFormDialog from './components/ProductFormDialog';

const ALL_CATEGORIES = 'all';

/**
 * Product Library — STOCK.md §8.
 *
 * Products are never deleted, only toggled Active/Inactive: an inactive product must
 * still render on the historical orders that reference it, while disappearing from
 * every selector.
 *
 * Bulk CSV import (§8) is NOT built yet — it needs a column mapping and a dry-run/
 * conflict step to be safe against a 283-row catalogue, which is more than a button.
 */
export default function ProductLibraryPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_PRODUCT);

    const [products, setProducts] = useState<Product[]>([]);
    const [options, setOptions] = useState<ProductOptions>({ categories: [], uoms: [] });
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<ListStatusFilter>('active');
    const [category, setCategory] = useState<string>(ALL_CATEGORIES);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<Product | null>(null);

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            setProducts(await productsApi.list({
                status,
                category: category === ALL_CATEGORIES ? undefined : category,
            }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load products');
        } finally {
            setLoading(false);
        }
    }, [status, category]);

    useEffect(() => {
        if (canRead) fetchProducts();
    }, [canRead, fetchProducts]);

    useEffect(() => {
        if (!canRead) return;
        productsApi.options().then(setOptions).catch(() => undefined);
    }, [canRead]);

    // Aliases are searchable too — the yard searches by slang, not by SKU.
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return products;
        return products.filter((p) =>
            [p.name, p.sku, p.size, p.category, ...(p.aliases ?? []).map((a) => a.alias)]
                .filter(Boolean)
                .some((v) => (v as string).toLowerCase().includes(q))
        );
    }, [products, search]);

    const handleToggleStatus = async (product: Product) => {
        try {
            await productsApi.setStatus(product.id, !product.isActive);
            toast.success(`${product.name} set to ${product.isActive ? 'Inactive' : 'Active'}`);
            fetchProducts();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update status');
        }
    };

    const handleExport = async (format: 'csv' | 'excel') => {
        try {
            await productsApi.export(format);
            toast.success(`Exported as ${format.toUpperCase()}`);
        } catch {
            toast.error('Failed to generate export file');
        }
    };

    const openAdd = () => { setEditing(null); setDialogOpen(true); };
    const openEdit = (product: Product) => { setEditing(product); setDialogOpen(true); };

    const money = (v: number | null) =>
        v === null || v === undefined ? '–' : `$${v.toFixed(2)}`;

    if (!canRead) return <AccessRestricted />;

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Product Library</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        The master list every order type selects from. Shared across all yards.
                    </p>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-2 w-full md:w-auto">
                    <Can I={ACTIONS.EXPORT} a={RESOURCES.STOCK_PRODUCT}>
                        <Button
                            variant="outline"
                            onClick={() => handleExport('csv')}
                            className="flex items-center gap-2 w-full md:w-auto border-slate-200 hover:bg-slate-50"
                        >
                            <FileDown size={16} /><span>Export CSV</span>
                        </Button>
                    </Can>
                    <Can I={ACTIONS.CREATE} a={RESOURCES.STOCK_PRODUCT}>
                        <Button
                            onClick={openAdd}
                            className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                        >
                            <Plus size={16} /><span>Add Product</span>
                        </Button>
                    </Can>
                </div>
            </div>

            {/* Active / Inactive */}
            <Tabs value={status} onValueChange={(v) => setStatus(v as ListStatusFilter)} className="w-full">
                <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
                    <TabsTrigger value="active" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Active
                    </TabsTrigger>
                    <TabsTrigger value="inactive" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Inactive
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {/* Search + category filter */}
            <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                <div className="relative w-full md:flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by name, SKU or alias..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-full"
                    />
                </div>
                <div className="w-full md:w-[220px] min-w-0">
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="w-full bg-white truncate overflow-hidden">
                            <SelectValue placeholder="Filter by Category" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_CATEGORIES}>All Categories</SelectItem>
                            {options.categories.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <span className="text-sm text-muted-foreground">{filtered.length} records</span>
            </div>

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">No products found</div>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {filtered.map((p) => (
                            <Card key={p.id} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <Package size={16} className="text-muted-foreground shrink-0" />
                                            <span className="font-medium truncate">{p.name}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1 font-mono">{p.sku}</p>
                                    </div>
                                    <StatusPill isActive={p.isActive} />
                                </div>
                                <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                                    <p>{p.category} · {p.uom}{p.size ? ` · ${p.size}` : ''}</p>
                                    <p>Replacement: {money(p.replacementValue)}</p>
                                    {(p.aliases?.length ?? 0) > 0 && (
                                        <p className="flex items-center gap-1 flex-wrap pt-1">
                                            <Sparkles size={12} />
                                            {p.aliases.map((a) => a.alias).join(', ')}
                                        </p>
                                    )}
                                </div>
                                <div className="flex justify-end gap-2 mt-3">
                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.STOCK_PRODUCT}>
                                        <Button variant="outline" size="icon" onClick={() => openEdit(p)} aria-label="Edit">
                                            <Edit size={16} />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleToggleStatus(p)}
                                            aria-label={p.isActive ? 'Set inactive' : 'Set active'}
                                            className={cn(p.isActive && 'text-destructive')}
                                        >
                                            <Power size={16} />
                                        </Button>
                                    </Can>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Product</TableHead>
                                        <TableHead className="font-semibold text-slate-900">SKU</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Category</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Unit</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Size</TableHead>
                                        <TableHead className="font-semibold text-slate-900 text-right">Replacement</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Aliases</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                        <TableHead className="font-semibold text-slate-900 text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((p) => (
                                        <TableRow key={p.id}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Package size={16} className="text-muted-foreground" />
                                                    {p.name}
                                                </div>
                                            </TableCell>
                                            <TableCell className="font-mono text-muted-foreground">{p.sku}</TableCell>
                                            <TableCell>{p.category}</TableCell>
                                            <TableCell>{p.uom}</TableCell>
                                            <TableCell>{p.size ?? <span className="text-muted-foreground">–</span>}</TableCell>
                                            <TableCell className="text-right">{money(p.replacementValue)}</TableCell>
                                            <TableCell className="whitespace-normal max-w-[220px]">
                                                {(p.aliases?.length ?? 0) === 0 ? (
                                                    <span className="text-muted-foreground">–</span>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground">
                                                        {p.aliases.map((a) => a.alias).join(', ')}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell><StatusPill isActive={p.isActive} /></TableCell>
                                            <TableCell>
                                                <div className="flex justify-end gap-2">
                                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.STOCK_PRODUCT}>
                                                        <Button variant="outline" size="icon" onClick={() => openEdit(p)} aria-label="Edit">
                                                            <Edit size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => handleToggleStatus(p)}
                                                            aria-label={p.isActive ? 'Set inactive' : 'Set active'}
                                                            className={cn(p.isActive && 'text-destructive')}
                                                        >
                                                            <Power size={16} />
                                                        </Button>
                                                    </Can>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}

            <ProductFormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                product={editing}
                options={options}
                onSaved={fetchProducts}
            />
        </div>
    );
}
